/**
 * Trending Calculations Utility
 * Functions for calculating 6-month rolling averages and performance comparisons
 * Created: 2025-11-17
 */

/**
 * Get last N complete months (excluding current month)
 * @param {string} currentMonth - Current month name ('November')
 * @param {number} n - Number of months to retrieve (default 3)
 * @param {number} currentYear - Current year (default to current year)
 * @returns {Array<{month: string, year: number}>} - Array of month+year objects
 */
export function getLastNCompleteMonths(currentMonth, n = 3, currentYear = new Date().getFullYear()) {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  const currentMonthIndex = monthNames.indexOf(currentMonth);
  if (currentMonthIndex === -1) {
    console.error(`Invalid month name: ${currentMonth}`);
    return [];
  }

  const lastMonths = [];
  let year = currentYear;
  for (let i = 1; i <= n; i++) {
    let targetIndex = currentMonthIndex - i;
    if (targetIndex < 0) {
      targetIndex += 12; // Wrap to previous year
      year = currentYear - Math.ceil(i / 12); // Adjust year when wrapping
    }
    // Calculate correct year based on how many times we've wrapped
    const monthsBack = i;
    const yearsBack = Math.floor((currentMonthIndex - monthsBack + 12) < currentMonthIndex && (currentMonthIndex - monthsBack) < 0 ? 1 : 0);
    const actualYear = currentMonthIndex - i < 0 ? currentYear - 1 : currentYear;

    lastMonths.push({ month: monthNames[targetIndex], year: actualYear });
  }

  return lastMonths;
}

/**
 * Calculate total business days in a month (excludes weekends)
 * @param {number} year - Year (e.g., 2025)
 * @param {string} monthName - Month name ('October')
 * @returns {number} - Total business days (Mon-Fri) in that month
 */
export function getBusinessDaysInHistoricalMonth(year, monthName) {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  const monthIndex = monthNames.indexOf(monthName);
  if (monthIndex === -1) {
    console.error(`Invalid month name: ${monthName}`);
    return 0;
  }

  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  let businessDays = 0;

  for (let day = 1; day <= lastDay; day++) {
    const date = new Date(year, monthIndex, day);
    const dayOfWeek = date.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
  }

  return businessDays;
}

/**
 * Calculate 6-month rolling average for a specific metric
 * @param {object} data - Full dataset from WebAPI (data.tabs)
 * @param {string} rep - Rep name or 'All Reps'
 * @param {string} currentMonth - Current month name
 * @param {object} metricConfig - { tab: 'Dials_TalkTime', field: null (for count) or 'Revenue' (for sum) }
 * @param {number} currentYear - Current year for proper year filtering (default: current year)
 * @returns {object} - { avgPerDay, months, monthlyValues, totalBusinessDays }
 */
export function calculate6MonthRollingAverage(data, rep, currentMonth, metricConfig, currentYear = new Date().getFullYear()) {
  const { tab, field, fields } = metricConfig;

  // Get last 6 complete months with year info
  const months = getLastNCompleteMonths(currentMonth, 6, currentYear);

  // FAST PATH: use pre-computed monthly aggregates for Dials_TalkTime and SMS
  // Reuses `months` already computed above. Falls through for all other tabs.
  if (data && data.monthlyAggregates && data.monthlyAggregates.length > 0 &&
      (tab === 'Dials_TalkTime' || tab === 'SMS') && !fields) {

    var fastTotal = 0;
    var fastBizDays = 0;
    var fastMonthlyValues = [];

    months.forEach(function(m) {
      var matchingRows = data.monthlyAggregates.filter(function(r) {
        var repMatch = (rep === 'All Reps' || rep === 'Team') ? true : r.Rep === rep;
        return r.Month === m.month && parseInt(r.Year) === m.year && repMatch;
      });

      var monthVal = 0;
      if (tab === 'Dials_TalkTime') {
        monthVal = matchingRows.reduce(function(sum, r) { return sum + (parseInt(r.TotalCalls) || 0); }, 0);
      } else {
        monthVal = matchingRows.reduce(function(sum, r) { return sum + (parseInt(r.TotalSMS) || 0); }, 0);
      }

      fastMonthlyValues.push(monthVal);
      if (monthVal > 0) { fastBizDays += getBusinessDaysInHistoricalMonth(m.year, m.month); }
      fastTotal += monthVal;
    });

    return {
      avgPerDay: fastBizDays > 0 ? fastTotal / fastBizDays : 0,
      months: months,
      monthlyValues: fastMonthlyValues,
      totalBusinessDays: fastBizDays
    };
  }
  // END FAST PATH — original logic below unchanged

  if (!data || !data.tabs || !data.tabs[tab]) {
    console.error(`Tab ${tab} not found in data`);
    return { avgPerDay: 0, months, monthlyValues: [], totalBusinessDays: 0 };
  }

  const tabData = data.tabs[tab].data || [];

  let totalValue = 0;
  let totalBusinessDays = 0;
  const monthlyValues = [];

  months.forEach(({ month, year }) => {
    // Filter by month, year (from Date column), and rep
    const filtered = tabData.filter(r => {
      const matchesMonth = r.Month === month;
      const matchesYear = r.Date && new Date(r.Date).getFullYear() === year;
      // Support both 'All Reps' and 'Team' for team view
      const matchesRep = (rep === 'All Reps' || rep === 'Team') || (r.Rep === rep);
      return matchesMonth && matchesYear && matchesRep;
    });

    // Calculate value for this month
    let monthValue;
    if (fields && Array.isArray(fields)) {
      // Sum multiple fields (e.g., Commission + Placement_Fee for revenue)
      monthValue = filtered.reduce((sum, r) => {
        return sum + fields.reduce((fieldSum, f) => fieldSum + (parseFloat(r[f]) || 0), 0);
      }, 0);
    } else if (field) {
      // Sum a specific field (e.g., Revenue)
      monthValue = filtered.reduce((sum, r) => sum + (parseFloat(r[field]) || 0), 0);
    } else {
      // Count rows (e.g., number of calls)
      monthValue = filtered.length;
    }

    monthlyValues.push(monthValue);

    // Get business days for this month (only if rep had activity)
    if (monthValue > 0) {
      const businessDays = getBusinessDaysInHistoricalMonth(year, month);
      totalBusinessDays += businessDays;
    }
    totalValue += monthValue;
  });

  // Calculate per-day average
  const avgPerDay = totalBusinessDays > 0 ? totalValue / totalBusinessDays : 0;

  return {
    avgPerDay,
    months,
    monthlyValues,
    totalBusinessDays,
    totalValue
  };
}

/**
 * Calculate conversion rates for a given month
 * @param {object} data - Full dataset from WebAPI
 * @param {string} rep - Rep name or 'All Reps'
 * @param {string} month - Month name
 * @returns {object} - All conversion rates
 */
export function calculateConversionRates(data, rep, month) {
  if (!data || !data.tabs) {
    return {
      callsToApps: 0,
      smsToApps: 0,
      appsToApproved: 0,
      approvedToFunded: 0,
      appsToFunded: 0,
      avgDealSize: 0
    };
  }

  // FAST PATH: use pre-computed monthly aggregates for calls and SMS counts.
  // When raw Dials_TalkTime / SMS tabs are removed, this prevents calls and sms from
  // always being 0. Falls through to the raw-tab path for apps/approved/funded/revenue.
  var calls, sms;
  if (data.monthlyAggregates && data.monthlyAggregates.length > 0) {
    var aggRows = data.monthlyAggregates.filter(function(r) {
      var repMatch = (rep === 'All Reps' || rep === 'Team') ? true : r.Rep === rep;
      return r.Month === month && repMatch;
    });
    calls = aggRows.reduce(function(sum, r) { return sum + (parseInt(r.TotalCalls) || 0); }, 0);
    sms   = aggRows.reduce(function(sum, r) { return sum + (parseInt(r.TotalSMS)   || 0); }, 0);
  }

  // Helper function to filter by month and rep
  const filterData = (tabName) => {
    const tabData = data.tabs[tabName]?.data || [];
    return tabData.filter(r => {
      const matchesMonth = r.Month === month;
      // Support both 'All Reps' and 'Team' for team view
      const matchesRep = (rep === 'All Reps' || rep === 'Team') || (r.Rep === rep);
      return matchesMonth && matchesRep;
    });
  };

  // Get counts for each stage — use aggregate values when available, otherwise raw tabs
  if (calls === undefined) { calls = filterData('Dials_TalkTime').length; }
  if (sms === undefined)   { sms   = filterData('SMS').reduce((sum, r) => sum + (parseFloat(r.Count) || 0), 0); }
  const apps = filterData('Apps_In').length;
  const approved = filterData('Approved').length;
  const funded = filterData('Funded').length;
  const revenue = filterData('Funded').reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);

  // Calculate conversion rates
  const callsToApps = calls > 0 ? (apps / calls) * 100 : 0;
  const smsToApps = sms > 0 ? (apps / sms) * 100 : 0;
  const appsToApproved = apps > 0 ? (approved / apps) * 100 : 0;
  const approvedToFunded = approved > 0 ? (funded / approved) * 100 : 0;
  const appsToFunded = apps > 0 ? (funded / apps) * 100 : 0;
  const avgDealSize = funded > 0 ? revenue / funded : 0;

  return {
    callsToApps: Math.round(callsToApps * 10) / 10, // Round to 1 decimal
    smsToApps: Math.round(smsToApps * 10) / 10,
    appsToApproved: Math.round(appsToApproved * 10) / 10,
    approvedToFunded: Math.round(approvedToFunded * 10) / 10,
    appsToFunded: Math.round(appsToFunded * 10) / 10,
    avgDealSize: Math.round(avgDealSize * 100) / 100, // Round to 2 decimals
    counts: { calls, sms, apps, approved, funded, revenue } // Include raw counts
  };
}

/**
 * Compare current metric to historical average
 * @param {number} currentValue - Current month value
 * @param {number} historicalAvg - 6-month rolling average
 * @returns {object} - Comparison with variance and trend
 */
export function compareMetrics(currentValue, historicalAvg) {
  const variance = currentValue - historicalAvg;
  const variancePercent = historicalAvg !== 0
    ? (variance / historicalAvg) * 100
    : 0;

  // Determine trend (threshold: ±5%)
  let trend = 'stable';
  let indicator = '→';

  if (variancePercent > 5) {
    trend = 'up';
    indicator = '↗';
  } else if (variancePercent < -5) {
    trend = 'down';
    indicator = '↘';
  }

  return {
    value: Math.round(currentValue * 100) / 100,
    historicalAvg: Math.round(historicalAvg * 100) / 100,
    variance: Math.round(variance * 100) / 100,
    variancePercent: Math.round(variancePercent * 10) / 10,
    trend,
    indicator
  };
}

/**
 * Generate automated performance diagnosis
 * @param {object} comparisons - Object with all metric comparisons
 * @param {object} conversionRates - Current and historical conversion rates
 * @returns {object} - Diagnosis with positive/negative changes and insights
 */
export function generateAutomatedDiagnosis(comparisons, conversionRates) {
  const positiveChanges = [];
  const negativeChanges = [];

  // Analyze each metric
  Object.keys(comparisons).forEach(metric => {
    const comp = comparisons[metric];
    const metricLabel = formatMetricLabel(metric);

    if (comp.variancePercent > 5) {
      // Positive change
      positiveChanges.push({
        metric: metricLabel,
        change: `+${Math.abs(comp.variance).toFixed(1)}`,
        percent: `+${comp.variancePercent.toFixed(1)}%`,
        description: generateChangeDescription(metric, 'increase')
      });
    } else if (comp.variancePercent < -5) {
      // Negative change
      negativeChanges.push({
        metric: metricLabel,
        change: `${comp.variance.toFixed(1)}`,
        percent: `${comp.variancePercent.toFixed(1)}%`,
        description: generateChangeDescription(metric, 'decrease')
      });
    }
  });

  // Generate overall diagnosis
  const diagnosis = generateDiagnosisText(positiveChanges, negativeChanges, conversionRates);

  // Generate action items
  const actionItems = generateActionItems(negativeChanges, conversionRates);

  return {
    positiveChanges,
    negativeChanges,
    diagnosis,
    actionItems
  };
}

/**
 * Format metric name for display
 */
function formatMetricLabel(metric) {
  const labels = {
    callsPerDay: 'Calls/day',
    smsPerDay: 'SMS/day',
    appsPerDay: 'Apps/day',
    approvedPerDay: 'Approved/day',
    fundedPerDay: 'Funded/day',
    revenuePerDay: 'Revenue/day'
  };
  return labels[metric] || metric;
}

/**
 * Generate description for a metric change
 */
function generateChangeDescription(metric, direction) {
  const descriptions = {
    callsPerDay: direction === 'increase'
      ? 'More outreach activity'
      : 'Reduced call volume',
    smsPerDay: direction === 'increase'
      ? 'Increased text outreach'
      : 'Fewer text messages sent',
    appsPerDay: direction === 'increase'
      ? 'Higher application rate'
      : 'Trailing historical pace',
    approvedPerDay: direction === 'increase'
      ? 'More approvals'
      : 'Fewer approvals',
    fundedPerDay: direction === 'increase'
      ? 'Better close rate'
      : 'Lower funding rate',
    revenuePerDay: direction === 'increase'
      ? 'Revenue growth'
      : 'Revenue declining'
  };
  return descriptions[metric] || (direction === 'increase' ? 'Improving' : 'Declining');
}

/**
 * Generate overall diagnosis text
 */
function generateDiagnosisText(positiveChanges, negativeChanges, conversionRates) {
  if (positiveChanges.length === 0 && negativeChanges.length === 0) {
    return "Performance is stable compared to the 6-month average. All key metrics are within expected ranges.";
  }

  let diagnosis = '';

  // Summarize positive changes
  if (positiveChanges.length > 0) {
    const topPositive = positiveChanges[0].metric;
    diagnosis += `${topPositive} is up ${positiveChanges[0].percent}. `;
  }

  // Summarize negative changes
  if (negativeChanges.length > 0) {
    const topNegative = negativeChanges[0].metric;
    diagnosis += `However, ${topNegative} is down ${negativeChanges[0].percent}. `;
  }

  // Add conversion insight
  if (conversionRates.current && conversionRates.historical) {
    const callsToAppsChange = conversionRates.current.callsToApps - conversionRates.historical.callsToApps;
    if (Math.abs(callsToAppsChange) > 0.5) {
      if (callsToAppsChange > 0) {
        diagnosis += `Calls→Apps conversion is improving (+${callsToAppsChange.toFixed(1)}pp), showing better lead quality or sales technique. `;
      } else {
        diagnosis += `Calls→Apps conversion is declining (${callsToAppsChange.toFixed(1)}pp), suggesting leads may be lower quality or pitch needs refinement. `;
      }
    }
  }

  return diagnosis || "Performance metrics are mixed compared to historical averages.";
}

/**
 * Generate actionable recommendations
 */
function generateActionItems(negativeChanges, conversionRates) {
  const actions = [];

  negativeChanges.forEach(change => {
    if (change.metric === 'Calls/day') {
      actions.push(`Increase daily calls to match historical average (${Math.abs(parseFloat(change.change))} more calls/day)`);
    } else if (change.metric === 'Apps/day') {
      actions.push('Focus on lead generation activities to boost application rate');
    } else if (change.metric === 'Funded/day') {
      actions.push('Review recent declined approvals for patterns and follow up on pending deals');
    } else if (change.metric === 'Revenue/day') {
      actions.push('Analyze deal pipeline and close rate to identify revenue gaps');
    }
  });

  // Add conversion-specific actions
  if (conversionRates.current && conversionRates.historical) {
    const approvedToFundedChange = conversionRates.current.approvedToFunded - conversionRates.historical.approvedToFunded;
    if (approvedToFundedChange < -5) {
      actions.push('Investigate why approved deals are not funding (underwriting issues or customer hesitation)');
    }
  }

  // If no specific actions, provide general guidance
  if (actions.length === 0) {
    actions.push('Continue current performance and monitor trends');
  }

  return actions;
}

/**
 * Calculate current month MTD per-day rates
 * @param {object} data - Full dataset from WebAPI
 * @param {string} rep - Rep name or 'All Reps'
 * @param {string} currentMonth - Current month name
 * @param {number} businessDaysElapsed - Business days so far this month
 * @param {number} currentYear - Current year for proper filtering (default: current year)
 * @returns {object} - Per-day rates for all metrics
 */
export function calculateCurrentMonthRates(data, rep, currentMonth, businessDaysElapsed, currentYear = new Date().getFullYear()) {
  if (!data || !data.tabs || businessDaysElapsed === 0) {
    return {
      callsPerDay: 0,
      smsPerDay: 0,
      appsPerDay: 0,
      approvedPerDay: 0,
      fundedPerDay: 0,
      revenuePerDay: 0
    };
  }

  // Helper function to filter by current month, year, and rep
  const filterData = (tabName) => {
    const tabData = data.tabs[tabName]?.data || [];
    return tabData.filter(r => {
      const matchesMonth = r.Month === currentMonth;
      const matchesYear = r.Date && new Date(r.Date).getFullYear() === currentYear;
      // Support both 'All Reps' and 'Team' for team view
      const matchesRep = (rep === 'All Reps' || rep === 'Team') || (r.Rep === rep);
      return matchesMonth && matchesYear && matchesRep;
    });
  };

  // Get counts for each metric — handle both aggregate rows (TotalCalls/TotalSMS) and raw rows (.length/Count)
  const dialsRowsCR = filterData('Dials_TalkTime');
  const smsRowsCR = filterData('SMS');
  const calls = dialsRowsCR.length > 0 && dialsRowsCR[0].TotalCalls !== undefined
    ? dialsRowsCR.reduce((s, r) => s + (parseInt(r.TotalCalls) || 0), 0)
    : dialsRowsCR.length;
  const sms = smsRowsCR.length > 0 && smsRowsCR[0].TotalSMS !== undefined
    ? smsRowsCR.reduce((sum, r) => sum + (parseFloat(r.TotalSMS) || 0), 0)
    : smsRowsCR.reduce((sum, r) => sum + (parseFloat(r.Count) || 0), 0);
  const apps = filterData('Apps_In').length;
  const approved = filterData('Approved').length;
  const funded = filterData('Funded').length;
  const revenue = filterData('Funded').reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);

  return {
    callsPerDay: calls / businessDaysElapsed,
    smsPerDay: sms / businessDaysElapsed,
    appsPerDay: apps / businessDaysElapsed,
    approvedPerDay: approved / businessDaysElapsed,
    fundedPerDay: funded / businessDaysElapsed,
    revenuePerDay: revenue / businessDaysElapsed
  };
}

/**
 * Analyze conversion impact to identify worst performing conversion by revenue impact
 * Used by: Scenario B (Phase 4), Diagnosis component (Phase 5)
 *
 * @param {object} data - Full dataset from WebAPI
 * @param {string} rep - Rep name or 'All Reps' / 'Team'
 * @param {string} month - Current month name
 * @param {string[]} reps - Array of all rep names (for team filtering)
 * @param {number} businessDaysElapsed - Business days elapsed in current month
 * @param {number} year - Current year for proper filtering (default: current year)
 * @returns {object} - Conversion analysis with worst conversion, bright spots, issues
 */
export function analyzeConversionImpact(data, rep, month, reps, businessDaysElapsed, year = new Date().getFullYear()) {
  if (!data || !data.tabs || businessDaysElapsed === 0) {
    return null;
  }

  const isTeam = rep === 'All Reps' || rep === 'Team';

  // Helper to filter by rep
  const filterByRep = (records) => {
    if (!records) return [];
    if (isTeam) {
      return records.filter(r => reps.includes(r.Rep));
    }
    return records.filter(r => r.Rep === rep);
  };

  // Helper to filter by month AND year (using Date column)
  const filterByMonthYear = (dataArray) => {
    return dataArray?.filter(r => r.Month === month && r.Date && new Date(r.Date).getFullYear() === year) || [];
  };

  // Get current month data (with year filter)
  const callsRecords = filterByRep(filterByMonthYear(data.tabs.Dials_TalkTime?.data));
  const smsRecords = filterByRep(filterByMonthYear(data.tabs.SMS?.data));
  const appsRecords = filterByRep(filterByMonthYear(data.tabs.Apps_In?.data));
  const approvedRecords = filterByRep(filterByMonthYear(data.tabs.Approved?.data));
  const fundedRecords = filterByRep(filterByMonthYear(data.tabs.Funded?.data));

  // Current counts — handle both aggregate rows (TotalCalls/TotalSMS) and raw rows (.length/Count)
  const calls = callsRecords.length > 0 && callsRecords[0].TotalCalls !== undefined
    ? callsRecords.reduce((s, r) => s + (parseInt(r.TotalCalls) || 0), 0)
    : callsRecords.length;
  const sms = smsRecords.length > 0 && smsRecords[0].TotalSMS !== undefined
    ? smsRecords.reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0)
    : smsRecords.reduce((sum, r) => sum + (parseInt(r.Count) || 0), 0);
  const apps = appsRecords.length;
  const approved = approvedRecords.length;
  const funded = fundedRecords.length;
  const revenue = fundedRecords.reduce((sum, r) => sum + (parseFloat(r.Commission) || 0) + (parseFloat(r.Placement_Fee) || 0), 0);
  const activity = calls + sms + (apps * 5);

  // Edge case: not enough data
  if (apps === 0 || approved === 0 || funded === 0) {
    return {
      conversions: [],
      worstConversion: null,
      brightSpots: [],
      issues: [],
      totalLostRevenue: 0,
      primaryIssue: null,
      primaryImpact: 0
    };
  }

  // Current conversion rates
  const currentActivityToApps = activity > 0 ? (apps / activity) * 100 : 0;
  const currentAppsToApproved = apps > 0 ? (approved / apps) * 100 : 0;
  const currentApprovedToFunded = approved > 0 ? (funded / approved) * 100 : 0;
  const avgDealSize = funded > 0 ? revenue / funded : 0;

  // Get 6-month historical rates
  const historicalCalls = calculate6MonthRollingAverage(data, rep, month, { tab: 'Dials_TalkTime', field: null });
  const historicalSms = calculate6MonthRollingAverage(data, rep, month, { tab: 'SMS', field: 'Count' });
  const historicalApps = calculate6MonthRollingAverage(data, rep, month, { tab: 'Apps_In', field: null });
  const historicalApproved = calculate6MonthRollingAverage(data, rep, month, { tab: 'Approved', field: null });
  const historicalFunded = calculate6MonthRollingAverage(data, rep, month, { tab: 'Funded', field: null });
  const historicalRevenue = calculate6MonthRollingAverage(data, rep, month, { tab: 'Funded', fields: ['Commission', 'Placement_Fee'] });

  // Calculate historical conversion rates
  const histActivityPerDay = historicalCalls.avgPerDay + historicalSms.avgPerDay + (historicalApps.avgPerDay * 5);
  const histActivityToApps = histActivityPerDay > 0 ? (historicalApps.avgPerDay / histActivityPerDay) * 100 : 0;
  const histAppsToApproved = historicalApps.avgPerDay > 0 ? (historicalApproved.avgPerDay / historicalApps.avgPerDay) * 100 : 0;
  const histApprovedToFunded = historicalApproved.avgPerDay > 0 ? (historicalFunded.avgPerDay / historicalApproved.avgPerDay) * 100 : 0;

  // Calculate revenue impact for each conversion stage
  // IMPORTANT: Use CURRENT downstream rates to avoid double-counting

  // 1. Activity → Apps
  const activityToAppsGap = histActivityToApps - currentActivityToApps;
  let activityToAppsImpact = 0;
  let activityToAppsExtraDeals = 0;
  if (activityToAppsGap > 0) {
    const extraApps = activity * (activityToAppsGap / 100);
    const extraApproved = extraApps * (currentAppsToApproved / 100);
    const extraFunded = extraApproved * (currentApprovedToFunded / 100);
    activityToAppsImpact = extraFunded * avgDealSize;
    activityToAppsExtraDeals = extraFunded;
  }

  // 2. Apps → Approved
  const appsToApprovedGap = histAppsToApproved - currentAppsToApproved;
  let appsToApprovedImpact = 0;
  let appsToApprovedExtraDeals = 0;
  if (appsToApprovedGap > 0) {
    const extraApproved = apps * (appsToApprovedGap / 100);
    const extraFunded = extraApproved * (currentApprovedToFunded / 100);
    appsToApprovedImpact = extraFunded * avgDealSize;
    appsToApprovedExtraDeals = extraFunded;
  }

  // 3. Approved → Funded
  const approvedToFundedGap = histApprovedToFunded - currentApprovedToFunded;
  let approvedToFundedImpact = 0;
  let approvedToFundedExtraDeals = 0;
  if (approvedToFundedGap > 0) {
    const extraFunded = approved * (approvedToFundedGap / 100);
    approvedToFundedImpact = extraFunded * avgDealSize;
    approvedToFundedExtraDeals = extraFunded;
  }

  // Build conversion objects
  const conversions = [
    {
      name: 'Activity → Apps',
      key: 'activityToApps',
      currentRate: Math.round(currentActivityToApps * 10) / 10,
      historicalRate: Math.round(histActivityToApps * 10) / 10,
      gap: Math.round(activityToAppsGap * 10) / 10,
      revenueImpact: Math.round(activityToAppsImpact),
      extraDeals: Math.round(activityToAppsExtraDeals * 10) / 10,
      status: activityToAppsGap > 0.5 ? 'issue' : (activityToAppsGap < -0.5 ? 'bright_spot' : 'ok')
    },
    {
      name: 'Apps → Approved',
      key: 'appsToApproved',
      currentRate: Math.round(currentAppsToApproved * 10) / 10,
      historicalRate: Math.round(histAppsToApproved * 10) / 10,
      gap: Math.round(appsToApprovedGap * 10) / 10,
      revenueImpact: Math.round(appsToApprovedImpact),
      extraDeals: Math.round(appsToApprovedExtraDeals * 10) / 10,
      status: appsToApprovedGap > 0.5 ? 'issue' : (appsToApprovedGap < -0.5 ? 'bright_spot' : 'ok')
    },
    {
      name: 'Approved → Funded',
      key: 'approvedToFunded',
      currentRate: Math.round(currentApprovedToFunded * 10) / 10,
      historicalRate: Math.round(histApprovedToFunded * 10) / 10,
      gap: Math.round(approvedToFundedGap * 10) / 10,
      revenueImpact: Math.round(approvedToFundedImpact),
      extraDeals: Math.round(approvedToFundedExtraDeals * 10) / 10,
      status: approvedToFundedGap > 0.5 ? 'issue' : (approvedToFundedGap < -0.5 ? 'bright_spot' : 'ok')
    }
  ];

  // Sort by revenue impact (descending)
  const sortedByImpact = [...conversions].sort((a, b) => b.revenueImpact - a.revenueImpact);

  // Identify worst conversion (highest positive revenue impact)
  const issues = conversions.filter(c => c.status === 'issue').sort((a, b) => b.revenueImpact - a.revenueImpact);
  const brightSpots = conversions.filter(c => c.status === 'bright_spot');
  const worstConversion = issues.length > 0 ? issues[0] : null;

  // Calculate total lost revenue
  const totalLostRevenue = issues.reduce((sum, c) => sum + c.revenueImpact, 0);

  return {
    conversions,
    worstConversion,
    brightSpots,
    issues,
    totalLostRevenue,
    primaryIssue: worstConversion ? worstConversion.name : null,
    primaryImpact: worstConversion ? worstConversion.revenueImpact : 0
  };
}
